import { type ErrorEnvelope, isErrorEnvelope } from '@ccp/api-client';

/**
 * An error the interface can show.
 *
 * Every failure reaching a component is one of these, so an error state always has something to
 * display and a correlation id to quote in a support request.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly correlationId: string | undefined;
  readonly details: unknown;

  constructor(status: number, envelope: Partial<ErrorEnvelope> & { message?: string }) {
    super(envelope.message ?? 'The request failed.');
    this.name = 'ApiError';
    this.status = status;
    this.code = envelope.code ?? 'UNEXPECTED_ERROR';
    this.correlationId = envelope.correlationId;
    this.details = envelope.details;
  }

  /** Whether the person can do something about it, which decides retry or contact support. */
  get isRecoverable(): boolean {
    return this.status === 400 || this.status === 409 || this.status === 422;
  }
}

/** Turns whatever a failed response carried into an `ApiError`. */
export function toApiError(status: number, body: unknown): ApiError {
  if (isErrorEnvelope(body)) {
    return new ApiError(status, body);
  }
  return new ApiError(status, { message: 'The request failed.' });
}
